import { X } from 'lucide-react';
import type { EventAwards } from '../../services/eventAwards';

interface AwardRevealModalProps {
  awards: EventAwards;
  attendeeId: string;
  onClose: () => void;
}

export default function AwardRevealModal({ awards, attendeeId, onClose }: AwardRevealModalProps) {
  const latest = awards.revealed.length > 0 ? awards.revealed[awards.revealed.length - 1] : null;
  const isMe = latest !== null && latest.winnerId === attendeeId;

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
      <div
        role="dialog"
        aria-modal="true"
        aria-label={latest ? `Award: ${latest.name}` : 'The awards are starting'}
        className={`bg-white dark:bg-gray-900 rounded-2xl shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto p-6 border-2 ${
          isMe ? 'border-amber-400' : 'border-transparent dark:border-gray-600'
        }`}
      >
        <div className="flex items-start justify-between gap-3 mb-2">
          <p className="text-xs font-semibold uppercase tracking-wide text-amber-600 dark:text-amber-400">
            {latest ? `🏆 Award ${awards.revealed.length} of ${awards.totalCount}` : '🏆 Event Awards'}
          </p>
          <button onClick={onClose} className="p-1.5 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800" aria-label="Close">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {latest ? (
          <div className="text-center space-y-2">
            <div className="text-6xl" aria-hidden="true">{latest.emoji}</div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">{latest.name}</h2>
            {latest.tagline && <p className="text-sm italic text-gray-500 dark:text-gray-400">{latest.tagline}</p>}
            <p className="text-xl font-semibold text-amber-600 dark:text-amber-400">{latest.winnerName}</p>
            {latest.stat && <p className="text-sm text-gray-600 dark:text-gray-300">{latest.stat}</p>}
            {isMe ? (
              <p className="pt-2 text-lg font-bold text-amber-600 dark:text-amber-400">That's you! 🎉</p>
            ) : (
              <p className="pt-2 text-xs text-gray-500 dark:text-gray-400">Every vote and request counted. Thanks for making tonight!</p>
            )}
          </div>
        ) : (
          <p className="py-6 text-center text-lg font-semibold text-gray-900 dark:text-white">
            🏆 The awards are starting! First one coming up…
          </p>
        )}

        <button
          onClick={onClose}
          className="mt-6 w-full px-4 py-2.5 rounded-lg bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-700 font-medium"
        >
          Close
        </button>
      </div>
    </div>
  );
}
